import { useEffect, useState } from "react";
import { useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import type { FollowedThread } from "../sync/types";
import { grouped } from "../timeline";
import { MessageItem } from "./MessageItem";
import { Badge, EmptyState, Page } from "./ui";
import { t, tn } from "../i18n";

// ThreadsView lists the threads one follows, those with new replies first
// to the eye: started, replied to or mentioned in. Seeing a thread here
// reads it.
export function ThreadsView() {
  const session = useSession();
  const state = useLantern();
  const [threads, setThreads] = useState<FollowedThread[] | null>(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    session.api.followedThreads().then(
      (r) => {
        if (!live) return;
        setThreads(r.threads);
        setMore(r.threads.length >= 20);
      },
      (err) => setError(String(err)),
    );
    return () => {
      live = false;
    };
  }, [session, state.threadNotices]);

  // Shown for a moment is read; the highlight stays until the next visit.
  useEffect(() => {
    if (!threads || document.visibilityState !== "visible") return;
    const t = setTimeout(() => {
      for (const th of threads) if (th.unread) session.markThreadRead(th.channel_id, th.root.id, Math.max(th.root.last_reply_seq ?? 0, th.replies.at(-1)?.seq ?? 0));
    }, 1500);
    return () => clearTimeout(t);
  }, [threads, session]);

  async function loadMore() {
    const last = threads?.at(-1);
    if (!last) return;
    const r = await session.api.followedThreads(last.last_reply_at);
    setThreads([...(threads ?? []), ...r.threads.filter((t) => !threads?.some((x) => x.root.id === t.root.id))]);
    setMore(r.threads.length >= 20);
  }

  return (
    <Page className="threads-view" title={t("Threads")} description={t("Threads you started, replied to or were mentioned in, newest activity first.")}>
      {error && <p className="error">{error}</p>}
      {threads && threads.length === 0 && (
        <EmptyState icon="💬" title={t("No threads yet")}>
          {t("Reply to a message, or be mentioned in a reply, and the thread shows up here.")}
        </EmptyState>
      )}
      {threads?.map((th) => {
        const open = () => navigate(`/c/${th.channel_id}/t/${th.root.id}`);
        const hidden = (th.root.reply_count ?? 0) - th.replies.length;
        return (
          <article key={th.root.id} className={`card thread-card ${th.unread ? "unread" : ""}`} aria-label={t("Thread in {channel}", { channel: session.channelName(th.channel_id) })}>
            <header className="thread-card-header">
              <button className="link" onClick={() => navigate(`/c/${th.channel_id}`)}>
                {session.channelName(th.channel_id) || t("a conversation")}
              </button>
              <span className="muted">
                {tn(th.root.reply_count ?? 0, "{n} reply", "{n} replies")}
              </span>
              {th.unread && <Badge tone="accent">{t("New replies")}</Badge>}
            </header>
            <MessageItem m={th.root} />
            {hidden > 0 && (
              <button className="more-replies" onClick={open}>
                {tn(hidden, "{n} more reply", "{n} more replies")}
              </button>
            )}
            {th.replies.map((m, i) => (
              <MessageItem key={m.id} m={m} compact={grouped(th.replies[i - 1], m)} />
            ))}
            <footer className="thread-card-footer">
              <button className="small" onClick={open}>
                {t("Reply")}
              </button>
            </footer>
          </article>
        );
      })}
      {more && (
        <div className="form-actions">
          <button onClick={() => void loadMore()}>{t("Show older threads")}</button>
        </div>
      )}
    </Page>
  );
}
